import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

const API_BASE = 'https://alcobrakeadmin.com/v3/us/checklist';

/* =========================================================
   HARDWARE ROOM / PRODUCTION - 17 ITEMS
   ========================================================= */

const hardwareItems = [
  [1, 'Approved firmware flashed and firmware version verified.'],
  [2, 'Device calibration completed and calibration values verified.'],
  [3, 'Device added and registered in the system.'],
  [4, 'Device assigned to the correct client.'],
  [5, 'SIM card inserted and 4G/server connectivity verified.'],
  [6, 'Calibration data, offset values, logs, images, relay operation, BLE connection, and GPS verified.'],
  [7, 'Device connected to the 12V/24V test setup and proper power-up verified.'],
  [8, 'Ignition/Key ON detection verified and Initial Test request confirmed.'],
  [9, 'Initial Test PASS verified including result, image, GPS, event upload, relay operation, and engine start.'],
  [10, 'Initial Test FAIL verified including result, event upload, engine blocking, lockout time, and retest request. (ACCURACY TEST)'],
  [11, 'Engine ON status verified after a successful Initial Test.'],
  [12, 'Random/Rolling Test PASS verified including result, image, GPS, event upload, and normal vehicle operation.'],
  [13, 'Random/Rolling Test FAIL verified including result, image, GPS, event upload, warning, and retest timer.'],
  [14, 'All generated events, timestamps, BAC values, PASS/FAIL results, images, and GPS data verified in the backend.'],
  [15, 'Final physical and functional inspection of the device completed.'],
  [16, 'Device confirmed free from pending hardware, firmware, configuration, rework, or debugging issues.'],
  [17, 'Device marked READY FOR FURTHER TESTING only after all production and testing checks have passed.']
];

/* =========================================================
   FINAL FUNCTIONAL TESTING - A
   ========================================================= */

const finalA = [
  [1, 'Sample disconnection detection verified and correct device response confirmed.'],
  [2, 'All OTP functions verified including OTP generation, validation, and usage.'],
  [3, 'All 4G commands verified and correct device execution confirmed.'],
  [4, 'All Wi-Fi commands verified and correct device execution confirmed.'],
  [5, 'Car/vehicle battery tampering detection verified and corresponding event confirmed.'],
  [6, 'Device REFUSAL function verified and corresponding event confirmed.'],
  [7, 'Complete log records verified in the Dealer App and Desktop application.'],
  [8, 'Display verified for all screens, messages, values, and indications.'],
  [9, 'All LEDs verified for correct indication in each applicable device state.'],
  [10, 'Buzzer verified for correct sound/alert in all applicable conditions.'],
  [11, 'Complete device change/replacement process verified and calibration data update confirmed.']
];

/* =========================================================
   FINAL FUNCTIONAL TESTING - B
   ========================================================= */

const finalB = [
  [12, 'Violation Reset command verified and expected device response confirmed.'],
  [13, 'Device Configuration command verified and all configuration values confirmed.'],
  [14, 'Take Test command verified and breath-test flow confirmed.'],
  [15, 'Take Picture command verified and image capture/upload confirmed.'],
  [16, 'Restart command verified and device reconnection confirmed.'],
  [17, 'Set OTP command verified and OTP storage/usage confirmed.'],
  [18, 'OTP operation verified through Wi-Fi, 4G, and BLE.'],
  [19, 'Set Time command verified and device date/time update confirmed.'],
  [20, 'Tamper Enable/Disable commands verified.'],
  [21, 'Device Info command verified and expected device response confirmed.'],
  [22, 'Device Lock command verified.'],
  [23, 'Device Unlock command verified.'],
  [24, 'Memory Reset command verified.'],
  [25, 'Clear Storage command verified.'],
  [26, 'Bypass and Unbypass commands verified.'],
  [27, 'All Queue Commands verified for correct execution and sequence.']
];

/* =========================================================
   FINAL FUNCTIONAL TESTING - C
   ========================================================= */

const finalC = [
  [28, 'Proxy Enable/Disable verified.'],
  [29, 'Temperature Enable/Disable verified.'],
  [30, 'Barometric Enable/Disable verified.'],
  [31, 'Fail Timer operation verified.'],
  [32, 'Refusal Timer operation verified.'],
  [33, 'Engine-Off Timer operation verified.'],
  [34, 'Battery Tampering configuration and device response verified.'],
  [35, 'Insufficient Count Enable/Disable verified.'],
  [36, 'First Random Retest Timer verified.'],
  [37, 'Second Random Retest Timer verified.'],
  [38, 'Initial Lockout Timer verified.'],
  [39, 'First Initial Lockout Timer verified.'],
  [40, 'Second Initial Lockout Timer verified.'],
  [41, 'Device Result Show Enable/Disable verified.'],
  [42, 'Random Test FAIL/Refusal Timer behavior and subsequent retest flow verified.'],
  [43, 'Complete end-to-end test verified including state requirements, timers, commands, events, images, GPS data, and backend records.'],
  [44, 'Device marked FINAL TEST PASSED only when every test passes without any pending issue.'],
  [45, 'Any Final Testing failure recorded against the Device ID with the exact failed test and reason before rejection.'],
  [46, 'Device confirmed fully production-ready after successful Final Testing.']
];

/* =========================================================
   PRE-DISPATCH / NEW INSTALLATION - 13 ITEMS
   ========================================================= */

const dispatchItems = [
  [1, 'Hardware Room checklist confirmed PASS.'],
  [2, 'Final Functional Testing checklist confirmed PASS.'],
  [3, 'New Installation button clicked only after Final Testing is successfully completed.'],
  [4, 'Memory Reset completed successfully.'],
  [5, 'Clear Storage completed successfully.'],
  [6, 'All State Requirements reset to the approved default production values.'],
  [7, 'Device Configuration completed successfully.'],
  [8, 'Calibration Date created/updated correctly during Device Configuration.'],
  [9, 'Device confirmed to contain no previous testing data, temporary settings, or unwanted test records after New Installation.'],
  [10, 'Device confirmed in the default New Installation state.'],
  [11, 'Device confirmed ready for customer/vehicle installation.'],
  [12, 'Final physical condition checked before packing.'],
  [13, 'Device marked READY FOR PACKING & FINAL DISPATCH only after the New Installation process is successfully completed.']
];

/* =========================================================
   CHECKLIST TABLE
   ========================================================= */

const ChecklistTable = ({
  prefix,
  items,
  checklistState,
  remarks,
  accuracy,
  onResult,
  onRemark,
  onAccuracyChange
}) => {
  return (
    <div className="checklist-table-wrapper">
      <table className="device-checklist-table">

        <thead>
          <tr>
            <th>#</th>
            <th>Check Point</th>
            <th>Pass</th>
            <th>Fail</th>
            <th>Remarks</th>
          </tr>
        </thead>

        <tbody>
          {items.map(([number, text]) => {

            const result = checklistState[number] || '';

            const accuracyValues =
              prefix === 'hardware' && number === 10
                ? ['0.010', '0.020', '0.030', '0.050']
                : [];

            return (
              <tr
                key={`${prefix}-${number}`}
                className={
                  result === 'PASS'
                    ? 'checklist-row-pass'
                    : result === 'FAIL'
                      ? 'checklist-row-fail'
                      : ''
                }
              >

                <td>
                  {number}
                </td>

                <td>
                  <div className="checklist-point">
                    {text}
                  </div>

                  {accuracyValues.length > 0 && (
                    <div className="checklist-accuracy">

                      {accuracyValues.map((value) => (
                        <label key={value}>

                          <input
                            type="checkbox"
                            checked={
                              (accuracy[number] || []).includes(value)
                            }
                            onChange={() =>
                              onAccuracyChange(
                                number,
                                value
                              )
                            }
                          />

                          {value}

                        </label>
                      ))}

                    </div>
                  )}
                </td>

                <td className="checklist-status-cell">
                  <input
                    type="checkbox"
                    checked={result === 'PASS'}
                    onChange={() =>
                      onResult(
                        prefix,
                        number,
                        'PASS'
                      )
                    }
                  />
                </td>

                <td className="checklist-status-cell">
                  <input
                    type="checkbox"
                    checked={result === 'FAIL'}
                    onChange={() =>
                      onResult(
                        prefix,
                        number,
                        'FAIL'
                      )
                    }
                  />
                </td>

                <td>
                  <textarea
                    className="checklist-remarks"
                    value={remarks[number] || ''}
                    onChange={(event) =>
                      onRemark(
                        prefix,
                        number,
                        event.target.value
                      )
                    }
                    placeholder="Remarks"
                  />
                </td>

              </tr>
            );
          })}
        </tbody>

      </table>
    </div>
  );
};

/* =========================================================
   MAIN CHECKLIST MODAL
   ========================================================= */

const DeviceChecklistModal = ({
  isOpen,
  onClose,
  controlId,
  samplerId
}) => {

  const [activeTab, setActiveTab] = useState('hardware');

  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState('');

  const [messageType, setMessageType] = useState('ok');

  const [records, setRecords] = useState([]);

  const [checkedBy, setCheckedBy] = useState({
    hardware: '',
    final: '',
    dispatch: ''
  });

  const [checklistState, setChecklistState] = useState({
    hardware: {},
    final: {},
    dispatch: {}
  });

  const [remarks, setRemarks] = useState({
    hardware: {},
    final: {},
    dispatch: {}
  });

  const [accuracy, setAccuracy] = useState({});
  
const [changedItems, setChangedItems] = useState({});

  /* =========================================================
     MESSAGE
     ========================================================= */

  const showMessage = useCallback((text, type = 'ok') => {

    setMessage(text);
    setMessageType(type);

    setTimeout(() => {
      setMessage('');
    }, 4000);

  }, []);


  /* =========================================================
     CLEAR SCREEN
     ========================================================= */

 const clearAll = useCallback(() => {

  setChecklistState({
    hardware: {},
    final: {},
    dispatch: {}
  });

  setRemarks({
    hardware: {},
    final: {},
    dispatch: {}
  });

  setAccuracy({});

  setChangedItems({});   // <-- ADD HERE

  setCheckedBy({
    hardware: '',
    final: '',
    dispatch: ''
  });

}, []);


  /* =========================================================
     GET ITEMS
     ========================================================= */

  const getItems = (prefix) => {

    if (prefix === 'hardware') {
      return hardwareItems;
    }

    if (prefix === 'final') {
      return [
        ...finalA,
        ...finalB,
        ...finalC
      ];
    }

    return dispatchItems;
  };


  /* =========================================================
     GET DEPARTMENT
     ========================================================= */

  const getDepartment = (prefix) => {

    if (prefix === 'hardware') {
      return 'HARDWARE_ROOM';
    }

    if (prefix === 'final') {
      return 'FINAL_TESTING';
    }

    return 'PRE_DISPATCH';
  };


  /* =========================================================
     LOAD DEVICE
     ========================================================= */

  const loadDevice = useCallback(async () => {

    clearAll();
    setRecords([]);

    if (!controlId) {
      showMessage(
        'Control ID is required.',
        'err'
      );
      return;
    }

    setLoading(true);

    try {

      const url =
        `${API_BASE}/getbycontrolid/${encodeURIComponent(controlId)}`;

      console.log('CALLING CHECKLIST API:', url);

      const response = await fetch(url);

      const text = await response.text();

      console.log(
        'CHECKLIST STATUS:',
        response.status
      );

      console.log(
        'CHECKLIST RESPONSE:',
        text
      );

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}: ${text}`
        );
      }

      const json = JSON.parse(text);

      if (json?.success === false) {
        throw new Error(json.message || 'Unable to load checklist.');
      }

      const data =
        Array.isArray(json) ? json : Array.isArray(json?.data)
          ? json.data
          : [];

      setRecords(data);

      if (data.length === 0) {

        showMessage(
          'No saved checklist records found. You can start a new checklist.'
        );

        return;
      }


      const nextState = {
        hardware: {},
        final: {},
        dispatch: {}
      };

      const nextRemarks = {
        hardware: {},
        final: {},
        dispatch: {}
      };

      const nextCheckedBy = {
        hardware: '',
        final: '',
        dispatch: ''
      };


      data.forEach((item) => {

        let prefix = '';

        if (
          item.department ===
          'HARDWARE_ROOM'
        ) {
          prefix = 'hardware';
        }

        if (
          item.department ===
          'FINAL_TESTING'
        ) {
          prefix = 'final';
        }

        if (
          item.department ===
          'PRE_DISPATCH'
        ) {
          prefix = 'dispatch';
        }

        if (!prefix) {
          return;
        }

        const checklistNo =
          Number(item.checklist_no);

        nextState[prefix][checklistNo] =
          item.result || '';

        nextRemarks[prefix][checklistNo] =
          item.remarks || '';

        if (item.checked_by) {
          nextCheckedBy[prefix] =
            item.checked_by;
        }

      });


      setChecklistState(nextState);

      setRemarks(nextRemarks);

      setCheckedBy(nextCheckedBy);

      showMessage(
        `Loaded ${data.length} checklist records.`
      );

    } catch (error) {

      console.error(
        'CHECKLIST LOAD ERROR:',
        error
      );

      showMessage(
        `Load failed: ${error.message}`,
        'err'
      );

    } finally {

      setLoading(false);

    }

  }, [
    controlId,
    clearAll,
    showMessage
  ]);


  /* =========================================================
     LOAD WHEN MODAL OPENS
     ========================================================= */

  useEffect(() => {

    if (isOpen) {
      loadDevice();
    }

  }, [
    isOpen,
    loadDevice
  ]);


  /* =========================================================
     RESULT PASS / FAIL
     ========================================================= */

const setResult = (prefix, number, result) => {
  setChecklistState((previous) => ({
    ...previous,
    [prefix]: {
      ...previous[prefix],
      [number]:
        previous[prefix][number] === result ? '' : result
    }
  }));

  setChangedItems((previous) => ({
    ...previous,
    [`${prefix}-${number}`]: true
  }));
};


  /* =========================================================
     REMARKS
     ========================================================= */
const setRemark = (prefix, number, value) => {
  setRemarks((previous) => ({
    ...previous,
    [prefix]: {
      ...previous[prefix],
      [number]: value
    }
  }));

  setChangedItems((previous) => ({
    ...previous,
    [`${prefix}-${number}`]: true
  }));
};


  /* =========================================================
     ACCURACY
     ========================================================= */

  const setAccuracyValue = (
    number,
    value
  ) => {

    setAccuracy((previous) => {

      const current =
        previous[number] || [];

      const exists =
        current.includes(value);

      return {

        ...previous,

        [number]: exists
          ? current.filter(
              (item) => item !== value
            )
          : [
              ...current,
              value
            ]

      };

    });

  };


  /* =========================================================
     PROGRESS
     ========================================================= */

  const getProgress = (prefix) => {

    const items =
      getItems(prefix);

    return items.filter(
      ([number]) => {

        const result =
          checklistState[prefix][number];

        return (
          result === 'PASS' ||
          result === 'FAIL'
        );

      }
    ).length;

  };


  /* =========================================================
     SAVE DEPARTMENT
     ========================================================= */
const saveDepartment = async (prefix) => {
  if (!controlId) {
    showMessage('Control ID is required.', 'err');
    return;
  }

  if (!samplerId) {
    showMessage('Sampler ID is required.', 'err');
    return;
  }
 const personName = checkedBy[prefix]?.trim();

  if (!personName) {
    showMessage(
      prefix === 'hardware'
        ? 'Technician name is required.'
        : prefix === 'final'
          ? 'Tester name is required.'
          : 'Checked By name is required.',
      'err'
    );
    return;
  }
  const department = getDepartment(prefix);
  const items = getItems(prefix);

  // Only save items changed by the user
  const selected = items.filter(([number]) => {
    const result = checklistState[prefix][number];

    return (
      changedItems[`${prefix}-${number}`] === true &&
      (result === 'PASS' || result === 'FAIL')
    );
  });

  if (selected.length === 0) {
    showMessage('No new changes to save.', 'err');
    return;
  }

  setLoading(true);

  let saved = 0;
  let failed = 0;

  try {
    for (const [checklistNo] of selected) {
      const result =
        checklistState[prefix][checklistNo];

      let finalRemarks =
        remarks[prefix][checklistNo] || '';

      // Accuracy test for Hardware item 10
      if (
        prefix === 'hardware' &&
        checklistNo === 10
      ) {
        const selectedAccuracy =
          accuracy[checklistNo] || [];

        if (selectedAccuracy.length > 0) {
          finalRemarks =
            `${finalRemarks}${finalRemarks ? ' | ' : ''}` +
            `Accuracy Test: ${selectedAccuracy.join(', ')}`;
        }
      }

      const payload = {
        controlid: controlId,
        samplerid: samplerId,
        department,
        checklist_no: Number(checklistNo),
        result,
        remarks: finalRemarks,
        checked_by: checkedBy[prefix] || ''
      };

      try {
        // Check whether this checklist item already exists
        const existingRecord = records.find(
          (item) =>
            item.department === department &&
            Number(item.checklist_no) === Number(checklistNo)
        );

        const isExisting = !!existingRecord;

        // Existing -> edit
        // New -> add
        const url = isExisting
          ? `${API_BASE}/edit/${existingRecord.id}`
          : `${API_BASE}/add`;

        const response = await fetch(url, {
          method: isExisting ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const responseText = await response.text();

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}: ${responseText}`
          );
        }

        let json = {};

        if (responseText) {
          try {
            json = JSON.parse(responseText);
          } catch {
            json = {};
          }
        }

        if (json?.success === false) {
          throw new Error(
            json.message || 'Unable to save checklist item.'
          );
        }

        // Update local records
        if (isExisting) {
          setRecords((previous) =>
            previous.map((item) =>
              item.id === existingRecord.id
                ? {
                    ...item,
                    ...payload
                  }
                : item
            )
          );
        } else {
          setRecords((previous) => [
            ...previous,
            payload
          ]);
        }

        saved++;

      } catch (error) {
        console.error(
          `Save failed for checklist ${checklistNo}:`,
          error
        );

        failed++;
      }
    }

    // Clear changed flags for successfully processed items
    if (saved > 0) {
      setChangedItems((previous) => {
        const next = { ...previous };

        selected.forEach(([number]) => {
          delete next[`${prefix}-${number}`];
        });

        return next;
      });
    }

    if (failed === 0) {
      showMessage(
        `${saved} ${department} checklist item(s) saved successfully.`
      );
    } else {
      showMessage(
        `${saved} saved, ${failed} failed.`,
        'err'
      );
    }

  } finally {
    setLoading(false);
  }
};


  /* =========================================================
     RESET WHEN CLOSED
     ========================================================= */

  const handleClose = () => {

    setMessage('');

    onClose();

  };


  /* =========================================================
     DO NOT RENDER WHEN CLOSED
     ========================================================= */

  if (!isOpen) {
    return null;
  }


  /* =========================================================
     ACTIVE ITEMS
     ========================================================= */

  const activeItems =
    activeTab === 'hardware'
      ? hardwareItems
      : activeTab === 'final'
        ? [
            ...finalA,
            ...finalB,
            ...finalC
          ]
        : dispatchItems;


  const activeProgress =
    getProgress(activeTab);


  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div
      className="device-checklist-overlay"
      onMouseDown={handleClose}
    >

      <section
        className="device-checklist-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="device-checklist-title"
        onMouseDown={(event) =>
          event.stopPropagation()
        }
      >

        {/* =================================================
            HEADER
            ================================================= */}

        <header className="device-checklist-header">

          <div>

            <span>
              ALCOBRAKE DEVICE CHECKLIST
            </span>

            <h2 id="device-checklist-title">
              Device Production Checklist
            </h2>

            <p>
              Production, Final Functional Testing &amp; Pre-Dispatch
            </p>

          </div>


          <button
            type="button"
            className="device-checklist-close"
            onClick={handleClose}
            aria-label="Close checklist"
          >
            <X size={20} />
          </button>

        </header>


        {/* =================================================
            DEVICE INFO
            ================================================= */}

        <div className="device-checklist-device-info">

          <div>
            <span>Control ID</span>

            <strong>
              {controlId || '—'}
            </strong>
          </div>


          <div>
            <span>Sampler ID</span>

            <strong>
              {samplerId || '—'}
            </strong>
          </div>


          <div>
            <span>Loaded Records</span>

            <strong>
              {records.length}
            </strong>
          </div>

        </div>


        {/* =================================================
            MESSAGE
            ================================================= */}

        {message && (
          <div
            className={`device-checklist-message ${messageType}`}
          >
            {message}
          </div>
        )}


        {/* =================================================
            TABS
            ================================================= */}

        <div className="device-checklist-tabs">

          <button
            type="button"
            className={
              activeTab === 'hardware'
                ? 'active'
                : ''
            }
            onClick={() =>
              setActiveTab('hardware')
            }
          >
            1. Hardware Room / Production
          </button>


          <button
            type="button"
            className={
              activeTab === 'final'
                ? 'active'
                : ''
            }
            onClick={() =>
              setActiveTab('final')
            }
          >
            2. Final Functional Testing
          </button>


          <button
            type="button"
            className={
              activeTab === 'dispatch'
                ? 'active'
                : ''
            }
            onClick={() =>
              setActiveTab('dispatch')
            }
          >
            3. Pre-Dispatch / New Installation
          </button>

        </div>


        {/* =================================================
            BODY
            ================================================= */}

        <div className="device-checklist-body">

          <div className="checklist-department">


            {/* Department Header */}

            <div className="checklist-department-header">

              <h3>

                {activeTab === 'hardware'
                  ? 'Checklist 1 - Hardware Room / Production'
                  : activeTab === 'final'
                    ? 'Checklist 2 - Final Functional Testing'
                    : 'Checklist 3 - Pre-Dispatch / New Installation'}

              </h3>


              <span>
                Completed {activeProgress}/
                {activeItems.length}
              </span>

            </div>


            {/* Meta */}

            <div className="checklist-meta">

              <div>

                <label>
                  Date
                </label>

                <input
                  type="date"
                  defaultValue={
                    new Date()
                      .toISOString()
                      .split('T')[0]
                  }
                />

              </div>


              <div>

                <label>
                  {activeTab === 'hardware'
                    ? 'Technician'
                    : activeTab === 'final'
                      ? 'Tester'
                      : 'Checked By'}
                </label>

                <input
  required
  value={checkedBy[activeTab] || ''}
  onChange={(event) =>
    setCheckedBy((previous) => ({
      ...previous,
      [activeTab]: event.target.value
    }))
  }
  placeholder={
    activeTab === 'hardware'
      ? 'Technician name'
      : activeTab === 'final'
        ? 'Tester name'
        : 'Checked by'
  }
/>

              </div>


              <div>

                <label>
                  Department
                </label>

                <input
                  readOnly
                  value={
                    activeTab === 'hardware'
                      ? 'HARDWARE_ROOM'
                      : activeTab === 'final'
                        ? 'FINAL_TESTING'
                        : 'PRE_DISPATCH'
                  }
                />

              </div>

            </div>


            {/* =================================================
                HARDWARE
                ================================================= */}

            {activeTab === 'hardware' && (

              <ChecklistTable
                prefix="hardware"
                items={hardwareItems}
                checklistState={
                  checklistState.hardware
                }
                remarks={
                  remarks.hardware
                }
                accuracy={accuracy}
                onResult={setResult}
                onRemark={setRemark}
                onAccuracyChange={
                  setAccuracyValue
                }
              />

            )}


            {/* =================================================
                FINAL TESTING
                ================================================= */}

            {activeTab === 'final' && (
              <>

                <div className="checklist-section-title">
                  A. Device &amp; Functional Tests
                </div>

                <ChecklistTable
                  prefix="final"
                  items={finalA}
                  checklistState={
                    checklistState.final
                  }
                  remarks={
                    remarks.final
                  }
                  accuracy={accuracy}
                  onResult={setResult}
                  onRemark={setRemark}
                  onAccuracyChange={
                    setAccuracyValue
                  }
                />


                <div className="checklist-section-title">
                  B. Commands Testing
                </div>

                <ChecklistTable
                  prefix="final"
                  items={finalB}
                  checklistState={
                    checklistState.final
                  }
                  remarks={
                    remarks.final
                  }
                  accuracy={accuracy}
                  onResult={setResult}
                  onRemark={setRemark}
                  onAccuracyChange={
                    setAccuracyValue
                  }
                />


                <div className="checklist-section-title">
                  C. State Requirement &amp; Timer Testing
                </div>

                <ChecklistTable
                  prefix="final"
                  items={finalC}
                  checklistState={
                    checklistState.final
                  }
                  remarks={
                    remarks.final
                  }
                  accuracy={accuracy}
                  onResult={setResult}
                  onRemark={setRemark}
                  onAccuracyChange={
                    setAccuracyValue
                  }
                />

              </>
            )}


            {/* =================================================
                PRE-DISPATCH
                ================================================= */}

            {activeTab === 'dispatch' && (

              <ChecklistTable
                prefix="dispatch"
                items={dispatchItems}
                checklistState={
                  checklistState.dispatch
                }
                remarks={
                  remarks.dispatch
                }
                accuracy={accuracy}
                onResult={setResult}
                onRemark={setRemark}
                onAccuracyChange={
                  setAccuracyValue
                }
              />

            )}


            {/* =================================================
                ACTIONS
                ================================================= */}

            <div className="checklist-actions">

              <button
                type="button"
                className="checklist-save-btn"
                disabled={loading}
                onClick={() =>
                  saveDepartment(
                    activeTab
                  )
                }
              >

                {loading
                  ? 'Saving...'
                  : activeTab === 'hardware'
                    ? 'Save Hardware Checklist'
                    : activeTab === 'final'
                      ? 'Save Final Testing'
                      : 'Save Pre-Dispatch'}

              </button>


              {/* <button
                type="button"
                className="checklist-clear-btn"
                disabled={loading}
                onClick={() => {

                  setChecklistState(
                    (previous) => ({
                      ...previous,
                      [activeTab]: {}
                    })
                  );

                  setRemarks(
                    (previous) => ({
                      ...previous,
                      [activeTab]: {}
                    })
                  );

                  if (
                    activeTab === 'hardware'
                  ) {
                    setAccuracy({});
                  }

                  setCheckedBy(
                    (previous) => ({
                      ...previous,
                      [activeTab]: ''
                    })
                  );

                }}
              >
                Clear Screen
              </button> */}


              <div className="checklist-footer-note">
                Only completed production checklist items with a PASS or FAIL status will be recorded.
              </div>

            </div>

          </div>

        </div>


        {/* =================================================
            FOOTER
            ================================================= */}

        <footer className="device-checklist-footer">

          <span>
            {loading
              ? 'Processing checklist...'
              : 'Checklist data is saved per selected PASS/FAIL item.'}
          </span>


          <button
            type="button"
            onClick={handleClose}
          >
            Close
          </button>

        </footer>

      </section>

    </div>
  );
};

export default DeviceChecklistModal;
